import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Address from '@liveauctioneers/caterwaul-components/lib/Address/Address';
import ButtonLink from '@liveauctioneers/caterwaul-components/lib/ButtonLink/ButtonLink';
import { catalogLongCardDate } from '@/utils/dates';
import classNames from 'classnames';
import { fetchItemsIfNeeded } from '@/redux/modules/item';
import { FormattedMessage } from 'react-intl';
import { getAddressForCatalog } from '@/utils/address';
import { getCatalog } from '@/redux/modules/catalog';
import { getCatalogUrl } from '@/utils/urls';
import { getSeller } from '@/redux/modules/seller';
import isEqual from 'react-fast-compare';
import ItemCard from '@/components/ItemCard/ItemCard';
import ItemGrid from '@/components/ItemCard/ItemGrid';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import noop from '@/utils/noop';
import { safelyRender } from '@/utils/safelyRender';
import SeeAllLink from '../SeeAllLink/SeeAllLink';
import SideScroller from '@/components/SideScroller/SideScroller';
import styles from './ItemShelfCatalog.module.scss';
import Throbber from '@liveauctioneers/caterwaul-components/lib/Throbber/Throbber';
import useDeepCompareEffect from 'use-deep-compare-effect';
import useIsWiderThan from '@/hooks/useIsWiderThan';

const GRID_CHANGE_WIDTH = 550;

type Props = {
    allowBidding?: boolean;
    catalogId: number;
    className: string;
    cta: boolean;
    hideLiveButton?: boolean;
    identifier: string;
    itemIds: any[];
    links?: React.ReactNode;
    seeAllText?: React.ReactNode;
    seeAllUrl: string;
    showBidStatus?: boolean;
    showSoldBadge?: boolean;
    trackItemClick?: Function;
};

const ItemShelfCatalog = (props: Props) => {
    const dispatch = useDispatch();
    const {
        allowBidding,
        catalogId,
        className = '',
        cta = false,
        hideLiveButton,
        identifier,
        itemIds,
        links = false,
        seeAllText = '',
        seeAllUrl = '',
        showBidStatus = false,
        showSoldBadge = false,
        trackItemClick = noop,
    } = props;

    const wide = useIsWiderThan(GRID_CHANGE_WIDTH);

    useDeepCompareEffect(() => {
        if (itemIds.length) {
            dispatch(fetchItemsIfNeeded(itemIds, identifier));
        }
    }, [fetchItemsIfNeeded, itemIds, identifier]);

    const itemsString = itemIds.join(',');

    const cards = React.useMemo(() => {
        if (itemsString) {
            return itemsString.split(',').map((itemId: string, itemIndex: number) => {
                const key = `${itemIndex}-${identifier}`;
                return (
                    <ItemCard
                        allowBidding={allowBidding}
                        className={styles.item}
                        hideLiveButton={hideLiveButton}
                        hideLotNumbers
                        hideSeller
                        itemId={Number(itemId)}
                        key={key}
                        noWidthLimit
                        showBidStatus={showBidStatus}
                        showSoldBadge={showSoldBadge}
                        trackClick={() => trackItemClick(itemId)}
                    />
                );
            });
        }
        return [];
    }, [allowBidding, hideLiveButton, identifier, itemsString, showBidStatus, showSoldBadge, trackItemClick]);

    let content;
    let classes = classNames(styles.container, className);
    const catalog = useSelector((state) => getCatalog(state, catalogId), isEqual);
    const { saleStartTs, sellerId, title: catalogTitle } = catalog;
    const seller = useSelector((state) => getSeller(state, sellerId));
    const title = safelyRender(catalogTitle, 'span');

    const startDate = catalogLongCardDate(saleStartTs);

    const { address1, address2, city, countryCode, postalCode, stateCode } = getAddressForCatalog(catalog, seller);

    const date = (
        <div className={styles.date}>
            <span className={styles.start}>{startDate}</span>
            <span className={styles.pipe} />
            <Address
                address={{
                    address1,
                    address2,
                    city,
                    countryCode,
                    postalCode,
                    state: stateCode,
                }}
                cityState
                className={styles.address}
            />
        </div>
    );

    if (itemIds.length) {
        content = (
            <>
                <div className={styles.header}>
                    <div className={styles.title}>
                        <Link route to={getCatalogUrl(catalogId, catalogTitle)}>
                            <h2>{title}</h2>
                        </Link>
                        {wide && (
                            <SeeAllLink text={seeAllText || <FormattedMessage id="view_auction" />} url={seeAllUrl} />
                        )}
                    </div>
                </div>
                <div className={styles['catalog-info']}>{date}</div>
                {Boolean(links) && <div className={styles.links}>{links}</div>}
                {wide ? (
                    <SideScroller className={styles.carousel}>{cards}</SideScroller>
                ) : (
                    <>
                        <ItemGrid
                            className={styles.grid}
                            hideLotNumbers
                            hideSeller
                            itemCardClass={styles.card}
                            itemIds={itemIds.slice(0, 6)}
                            showBidStatusOnCards
                            showSoldBadge
                        />
                        {cta && (
                            <div className={styles.cta}>
                                <ButtonLink url={seeAllUrl}>
                                    <FormattedMessage id="view_auction" />
                                </ButtonLink>
                            </div>
                        )}
                    </>
                )}
            </>
        );
    } else {
        classes = classNames(styles.container, className, styles.wait);
        content = <Throbber />;
    }

    return (
        <div className={classes} id={identifier}>
            {Boolean(itemIds.length) && content}
        </div>
    );
};

export default React.memo<Props>(ItemShelfCatalog, isEqual);
